import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ARApiUrlBuilderService } from '@relayter/core';
import { NUC_BUTTON_TYPE, NucDialogConfigModel, NucDialogService, NucFullModalService } from '@relayter/rubber-duck';
import * as Sentry from '@sentry/angular';
import { BehaviorSubject, firstValueFrom, Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiConstants } from '../api/api.constant';
import { UsersApiService } from '../api/services/users.api.service';
import { AppConstants } from '../app.constants';
import { UserStorage } from '../classes/user-storage.class';
import { ChangePassword } from '../models/api/change-password.model';
import { LoginBodyModel } from '../models/api/login.model';
import { ENotificationDestination, ENotificationType } from '../models/api/notification.model';
import { UserDetailModel } from '../models/api/user.model';
import ErrorService from './error.service';
import { RLSegmentService } from './segment/rl-segment.service';

@Injectable({
    providedIn: 'root'
})
export class UserService {
    private readonly loggedInUserSubject: BehaviorSubject<UserDetailModel> = new BehaviorSubject<UserDetailModel>(null);
    private readonly router = inject(Router);
    private readonly dialogService = inject(NucDialogService);
    private readonly fullModalService = inject(NucFullModalService);
    private readonly segmentService = inject(RLSegmentService);
    private readonly userApiService = inject(UsersApiService);
    protected destroyRef = inject(DestroyRef);

    public initialise(): Observable<UserDetailModel> {
        // this is always called in the canActivate guard
        this.subscribeToUserUpdates();
        return this.userApiService.me().pipe(
            tap((user: UserDetailModel) => {
                this.loggedInUserSubject.next(user);
            }),
            takeUntilDestroyed(this.destroyRef)
        );
    }

    private subscribeToUserUpdates(): void {
        this.loggedInUserSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((user) => {
            this.segmentService.setUser(user);
            if (user) {
                Sentry.setUser({
                    teamName: user.team.name,
                    id: user._id,
                    teamId: user.team._id
                });
            } else {
                Sentry.setUser(null);
            }
        });
    }

    /**
     * Get Subject to listen for updates of the logged-in user.
     * @return {Observable<UserDetailModel>}
     */
    public getUserUpdates(): Observable<UserDetailModel> {
        return this.loggedInUserSubject.asObservable();
    }

    /**
     * Get the last announced user
     * @return {UserDetailModel}
     */
    public getLoggedInUser(): UserDetailModel {
        return this.loggedInUserSubject.getValue();
    }

    /**
     * Change the password for currently logged-in user and reset the access token in storage
     * @param changePassword
     * @return {Observable<boolean>}
     */
    public changePasswordMe(changePassword: ChangePassword): Observable<boolean> {
        return new Observable((obs) => {
            this.userApiService.changePasswordMe(changePassword).subscribe({
                next: (res: any) => {
                    obs.next(true);
                    obs.complete();
                },
                error: (error: unknown) => {
                    obs.error(error);
                }
            });
        });
    }

    /**
     * Login user at api and set accessToken
     */
    public loginWithCredentials(login: LoginBodyModel): Observable<void> {
        return new Observable((obs) => {
            this.userApiService.login(login).subscribe({
                next: () => {
                    obs.next();
                    obs.complete();
                },
                error: (error: unknown) => {
                    obs.error(error);
                }
            });
        });
    }

    /**
     * initiate auth at the sso idp by redirecting to the api sso endpoint
     */
    public redirectSso(ssoClientId: string, email: string, target: string = '/'): void {
        const params = {
            client_id: environment.OAUTH_CLIENT_ID,
            return_url: `${globalThis.location.origin.replace(/\/$/, '')}${AppConstants.CALLBACK_REDIRECT}?target=${encodeURIComponent(target)}`
        };

        if (email) {
            params['login_hint'] = email;
        }

        const loginUrl = ARApiUrlBuilderService.urlFromComponents(
            [environment.API_SERVER, ApiConstants.API_BASE_PATH, ApiConstants.API_GROUP_SSO_CLIENTS, ssoClientId, ApiConstants.API_METHOD_AUTHORIZE],
            params
        );

        globalThis.location.assign(loginUrl);
    }

    /**
     * Log out of in Relayter api, clear local user storage and redirect to login page
     */
    public async logout() {
        await firstValueFrom(this.userApiService.logout());
        this.clearUserAndRedirectToLogin();
    }

    /**
     * Empty local storage
     */
    public clearUserStorage(): void {
        UserStorage.removeUser();
        this.loggedInUserSubject.next(null); // notify listeners (socket manager)
        UserStorage.removeAccessToken();
    }

    /**
     * Clear the user storage and redirect to login page.
     * Use this if the token does not work. Otherwise, use {@link logout}
     * @param {string} returnUrl
     */
    public clearUserAndRedirectToLogin(returnUrl?: string): void {
        this.clearUserStorage();

        const options = { state: { skipGuards: true } };
        if (returnUrl) {
            options['queryParams'] = { returnUrl };
        }

        this.router.navigate([AppConstants.LOGIN_REDIRECT], options).catch(ErrorService.handleError);
    }

    /**
     * Log out user with session opened on other device
     * @param {NucDialogConfigModel} dialogConfig
     * @param {string} returnUrl
     *
     */
    public forcedSessionClose(dialogConfig: NucDialogConfigModel, returnUrl?: string): void {
        const confirmDialog = this.dialogService.openDialog(dialogConfig);
        dialogConfig.addAction('Confirm', NUC_BUTTON_TYPE.PRIMARY).subscribe(() => {
            confirmDialog.close();
        });
        confirmDialog.beforeClosed().subscribe(() => {
            // Close any opened full modal
            if (this.fullModalService.openedModal) {
                this.fullModalService.close();
            }
            this.clearUserAndRedirectToLogin(returnUrl);
        });
    }

    public hasNotificationPreference(type: ENotificationType, destination: ENotificationDestination): boolean {
        return !!this.loggedInUserSubject
            .getValue()
            ?.preferences?.notifications?.find((notification) => notification.type === type && notification.options.includes(destination));
    }
}
